import createClient from 'openapi-fetch';
import type { paths } from './schema';
import { tokens } from '$lib/auth/tokens';

/** An API error with the machine-readable "code" from the ProblemDetails body, if any. */
export class ApiError extends Error {
	constructor(
		readonly status: number,
		readonly code: string | null
	) {
		super(code ?? `HTTP ${status}`);
	}
}

/** Adds the bearer token; on 401 refreshes the token once and retries. */
export async function authorizedFetch(request: Request): Promise<Response> {
	const retry = request.clone();
	const response = await fetch(withToken(request));
	if (response.status !== 401 || request.url.includes('/api/auth/')) {
		return response;
	}

	const refreshed = await tokens.refresh();
	return refreshed.kind === 'ok' ? fetch(withToken(retry)) : response;
}

function withToken(request: Request): Request {
	const token = tokens.get();
	if (token) request.headers.set('Authorization', `Bearer ${token}`);
	return request;
}

export const api = createClient<paths>({ baseUrl: '', fetch: authorizedFetch });

type ApiResult<T> = { data?: T; error?: unknown; response: Response };

/** Returns the response data or throws an {@link ApiError}. */
export async function unwrap<T>(call: Promise<ApiResult<T>>): Promise<T> {
	const { data, error, response } = await call;
	if (!response.ok) {
		throw new ApiError(response.status, errorCode(error));
	}
	return data as T;
}

function errorCode(error: unknown): string | null {
	if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') {
		return error.code;
	}
	return null;
}

/** True when the request never reached the server (offline, DNS, CORS …). */
export function isNetworkError(error: unknown): boolean {
	return error instanceof TypeError;
}
